import type {
  DetailEntry,
  EventCategory,
  EventSeverity,
  ParsedEvent,
  Upload,
  UploadSummary,
} from "../../types/domain";
import {
  actionLabels,
  detailLabels,
  labelFor,
  subjectLabels,
  triggerLabels,
} from "./dictionaries";

export interface ParseContext {
  uploadId: string;
  companyId: string;
  createdAt?: string;
}

export interface ParseResult {
  events: ParsedEvent[];
  rowCount: number;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  summary: UploadSummary;
  stats: Pick<
    Upload,
    | "rowCount"
    | "dateRangeStart"
    | "dateRangeEnd"
    | "criticalIssueCount"
    | "warningCount"
    | "wateringEventCount"
    | "communicationEventCount"
    | "configurationChangeCount"
  >;
}

const wateringActions = new Set(["WT", "SO", "WA", "DN", "MR", "SR", "SP", "PA", "RL"]);

export class BaselineParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BaselineParseError";
  }
}

export function parseBaselineCsv(csvText: string, context: ParseContext): ParseResult {
  const rows = parseCsv(csvText);
  if (rows.length === 0) {
    throw new BaselineParseError("The CSV file is empty.");
  }

  const createdAt = context.createdAt ?? localIsoNow();
  const events: ParsedEvent[] = [];

  rows.forEach((columns, index) => {
    if (columns.every((column) => column.trim() === "")) return;
    if (columns.length < 4) {
      throw new BaselineParseError(`Row ${index + 1} does not have the required four columns.`);
    }

    const [dateText, subjectRaw, actionRaw, triggerRaw, ...detailColumns] = columns;
    const timestamp = parseControllerTimestamp(dateText.trim());
    if (!timestamp) {
      throw new BaselineParseError(`Row ${index + 1} has an unreadable timestamp.`);
    }

    const subjectCode = subjectRaw.trim().toUpperCase();
    const actionCode = actionRaw.trim().toUpperCase();
    const triggerCode = triggerRaw.trim().toUpperCase();
    const detailEntries = parseDetails(detailColumns);
    const rawDetails = detailEntries.reduce<Record<string, string | string[]>>((acc, detail) => {
      acc[detail.code] = detail.values.length > 1 ? detail.values : detail.rawValue;
      return acc;
    }, {});

    const eventBase = {
      subjectCode,
      actionCode,
      triggerCode,
      detailEntries,
      rawDetails,
      zoneNumber: getDetail(detailEntries, "ZN"),
      programNumber: getDetail(detailEntries, "PG"),
      pointOfConnection: getDetail(detailEntries, "PC"),
      masterValve: getDetail(detailEntries, "MV"),
      deviceSerialNumber: getDetail(detailEntries, "SN"),
      moistureSensor: subjectCode === "MS" ? getDetail(detailEntries, "SN") : undefined,
      errorText: [getDetail(detailEntries, "ER"), getDetail(detailEntries, "TX")]
        .filter(Boolean)
        .join(" ")
        .trim(),
    };

    const category = categorize(eventBase.subjectCode, eventBase.actionCode);
    const severity = severityFor(eventBase.subjectCode, eventBase.actionCode, eventBase.errorText);
    const language = buildLanguage({
      ...eventBase,
      category,
      severity,
    });

    events.push({
      eventId: `${context.uploadId}-${String(events.length + 1).padStart(5, "0")}`,
      uploadId: context.uploadId,
      companyId: context.companyId,
      timestamp,
      subjectCode,
      actionCode,
      triggerCode,
      subjectLabel: labelFor(subjectLabels, subjectCode),
      actionLabel: labelFor(actionLabels, actionCode),
      triggerLabel: labelFor(triggerLabels, triggerCode),
      rawColumns: columns,
      rawRow: columns.join(","),
      rawDetails,
      detailEntries,
      category,
      severity,
      title: language.title,
      summary: language.summary,
      plainEnglish: language.plainEnglish,
      zoneNumber: eventBase.zoneNumber,
      programNumber: eventBase.programNumber,
      pointOfConnection: eventBase.pointOfConnection,
      masterValve: eventBase.masterValve,
      deviceSerialNumber: eventBase.deviceSerialNumber,
      moistureSensor: eventBase.moistureSensor,
      errorText: eventBase.errorText || undefined,
      createdAt,
    });
  });

  if (events.length === 0) {
    throw new BaselineParseError("No readable event rows were found.");
  }

  events.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  const summary = summarizeUpload(context.uploadId, events);
  const stats = {
    rowCount: events.length,
    dateRangeStart: summary.dateRangeStart,
    dateRangeEnd: summary.dateRangeEnd,
    criticalIssueCount: summary.criticalIssues,
    warningCount: summary.warnings,
    wateringEventCount: events.filter((event) => event.category === "WATERING").length,
    communicationEventCount: events.filter((event) =>
      ["COMMUNICATION", "BASEMANAGER", "TWO_WIRE"].includes(event.category),
    ).length,
    configurationChangeCount: events.filter((event) => event.category === "CONFIGURATION")
      .length,
  };

  return {
    events,
    rowCount: events.length,
    dateRangeStart: summary.dateRangeStart,
    dateRangeEnd: summary.dateRangeEnd,
    summary,
    stats,
  };
}

export function parseCsv(input: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    const next = input[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(cell.trim());
      cell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(cell.trim());
      if (row.some((value) => value !== "")) rows.push(row);
      row = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  if (cell !== "" || row.length > 0) {
    row.push(cell.trim());
    if (row.some((value) => value !== "")) rows.push(row);
  }

  return rows;
}

export function parseControllerTimestamp(input: string) {
  const match = input.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!match) return undefined;
  const [, monthRaw, dayRaw, yearRaw, hourRaw, minuteRaw, secondRaw = "00"] = match;
  const yearNumber = Number(yearRaw);
  const year = yearRaw.length === 2 ? 2000 + yearNumber : yearNumber;
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  const second = Number(secondRaw);
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return undefined;
  }
  return `${year.toString().padStart(4, "0")}-${month
    .toString()
    .padStart(2, "0")}-${day.toString().padStart(2, "0")}T${hour
    .toString()
    .padStart(2, "0")}:${minute.toString().padStart(2, "0")}:${second
    .toString()
    .padStart(2, "0")}`;
}

function parseDetails(columns: string[]): DetailEntry[] {
  const entries: DetailEntry[] = [];
  for (const column of columns) {
    const trimmed = column.trim();
    if (!trimmed) continue;
    const separatorIndex = trimmed.indexOf("=");
    if (separatorIndex < 0) {
      entries.push({
        code: "VALUE",
        label: "Value",
        rawValue: trimmed,
        values: [trimmed],
      });
      continue;
    }
    const code = trimmed.slice(0, separatorIndex).trim().toUpperCase();
    const rawValue = trimmed.slice(separatorIndex + 1).trim();
    entries.push({
      code,
      label: labelFor(detailLabels, code),
      rawValue,
      values: rawValue.includes(";") ? rawValue.split(";").map((value) => value.trim()) : [rawValue],
    });
  }
  return entries;
}

export function getDetail(entries: DetailEntry[], code: string) {
  const entry = entries.find((detail) => detail.code === code);
  return entry?.rawValue || undefined;
}

export function categorize(subjectCode: string, actionCode: string): EventCategory {
  if (subjectCode === "TW") return "TWO_WIRE";
  if (subjectCode === "BM") return "BASEMANAGER";
  if (subjectCode === "MS") return "MOISTURE";
  if (["PC", "MV", "FM"].includes(subjectCode)) return "FLOW";
  if (actionCode === "CC") return "CONFIGURATION";
  if (wateringActions.has(actionCode)) return "WATERING";
  if (subjectCode === "PG") return "PROGRAM";
  if (subjectCode === "ZN") return "ZONE";
  if (actionCode === "ER") return "ERROR";
  if (subjectCode === "SY") return "SYSTEM";
  return "UNKNOWN";
}

export function severityFor(
  subjectCode: string,
  actionCode: string,
  errorText?: string,
): EventSeverity {
  if (subjectCode === "TW" && actionCode === "ER") return "CRITICAL";
  if (/no response/i.test(errorText ?? "")) return "CRITICAL";
  if (actionCode === "ER") return "WARNING";
  return "INFO";
}

function buildLanguage(event: {
  subjectCode: string;
  actionCode: string;
  triggerCode: string;
  detailEntries: DetailEntry[];
  zoneNumber?: string;
  programNumber?: string;
  pointOfConnection?: string;
  masterValve?: string;
  deviceSerialNumber?: string;
  errorText?: string;
  category: EventCategory;
  severity: EventSeverity;
}) {
  const subject = labelFor(subjectLabels, event.subjectCode);
  const action = labelFor(actionLabels, event.actionCode).toLowerCase();
  const trigger = labelFor(triggerLabels, event.triggerCode);
  const zone = event.zoneNumber;
  const program = event.programNumber;
  const poc = event.pointOfConnection;
  const masterValve = event.masterValve;
  const serial = event.deviceSerialNumber;
  const detailText = event.detailEntries
    .map((detail) => `${detail.label}: ${detail.rawValue || "blank"}`)
    .join("; ");

  if (event.subjectCode === "TW" && event.actionCode === "ER") {
    const noResponse = /no response/i.test(event.errorText ?? "");
    return {
      title: noResponse ? "Two-Wire no response" : "Two-Wire communication problem",
      summary: "The controller reported a two-wire communication issue.",
      plainEnglish: noResponse
        ? "The controller tried to communicate with a two-wire device and did not receive a response."
        : "The controller reported a two-wire communication issue. Check two-wire devices, wiring, and recent field changes.",
    };
  }

  if (event.subjectCode === "BM" && event.actionCode === "ER") {
    const error = getDetail(event.detailEntries, "ER");
    const text = getDetail(event.detailEntries, "TX");
    return {
      title: "BaseManager error",
      summary: `BaseManager reported an error${error ? `: ${error}` : ""}.`,
      plainEnglish: `BaseManager reported an error${error ? `: ${error}` : ""}.${
        text ? ` Additional message: ${text}.` : ""
      }`,
    };
  }

  if (event.actionCode === "ER") {
    return {
      title: `${subject} error`,
      summary: `${subject} reported an error${event.errorText ? `: ${event.errorText}` : ""}.`,
      plainEnglish: `${subject} reported an error${
        event.errorText ? `: ${event.errorText}` : ""
      }. Review the raw event details for the exact controller message.`,
    };
  }

  if (event.subjectCode === "PG" && event.actionCode === "WT") {
    return {
      title: `Program ${program ?? ""} watering`.trim(),
      summary: `Program ${program ?? "unknown"} was watering.`,
      plainEnglish: `Program ${program ?? "unknown"} was watering.`,
    };
  }

  if (event.subjectCode === "ZN" && event.actionCode === "WT") {
    return {
      title: `Zone ${zone ?? ""} watering`.trim(),
      summary: `Zone ${zone ?? "unknown"} was watering${program ? ` from Program ${program}` : ""}.`,
      plainEnglish: `Zone ${zone ?? "unknown"} was watering${
        program ? ` from Program ${program}` : ""
      }.`,
    };
  }

  if (event.subjectCode === "ZN" && event.actionCode === "SO") {
    return {
      title: `Zone ${zone ?? ""} entered soak`.trim(),
      summary: `Zone ${zone ?? "unknown"} entered soak${program ? ` from Program ${program}` : ""}.`,
      plainEnglish: `Zone ${zone ?? "unknown"} entered soak${
        program ? ` from Program ${program}` : ""
      }.`,
    };
  }

  if (event.subjectCode === "ZN" && event.actionCode === "WA") {
    return {
      title: `Zone ${zone ?? ""} waiting`.trim(),
      summary: `Zone ${zone ?? "unknown"} was waiting${program ? ` in Program ${program}` : ""}.`,
      plainEnglish: `Zone ${zone ?? "unknown"} was waiting${
        program ? ` in Program ${program}` : ""
      }.`,
    };
  }

  if (event.subjectCode === "PG" && event.actionCode === "WA") {
    return {
      title: `Program ${program ?? ""} waiting`.trim(),
      summary: `Program ${program ?? "unknown"} was waiting.`,
      plainEnglish: `Program ${program ?? "unknown"} was waiting for the next allowed watering step.`,
    };
  }

  if (event.actionCode === "DN") {
    const target = nameTarget(subject, { zone, program, poc, masterValve });
    return {
      title: `${target} done`,
      summary: `${target} finished.`,
      plainEnglish: `${target} finished its active controller operation.`,
    };
  }

  if (event.actionCode === "PA") {
    return {
      title: `${subject} paused`,
      summary: `${subject} was paused by ${trigger}.`,
      plainEnglish: `${subject} was paused. The trigger was ${trigger}.`,
    };
  }

  if (event.actionCode === "SP") {
    const target = nameTarget(subject, { zone, program, poc, masterValve });
    return {
      title: `${target} stopped`,
      summary: `${target} stopped.`,
      plainEnglish: `${target} stopped. The trigger was ${trigger}.`,
    };
  }

  if (event.actionCode === "SR") {
    const target = nameTarget(subject, { zone, program, poc, masterValve, serial });
    return {
      title: `${target} started`,
      summary: `${target} started from ${trigger}.`,
      plainEnglish: `${target} started. The trigger was ${trigger}.`,
    };
  }

  if (event.actionCode === "MR") {
    const target = nameTarget(subject, { zone, program, poc, masterValve });
    return {
      title: `${target} manual run`,
      summary: `${target} was manually run.`,
      plainEnglish: `${target} was manually run by the controller or a user action.`,
    };
  }

  if (event.actionCode === "RL") {
    const target = nameTarget(subject, { zone, program, poc, masterValve });
    return {
      title: `${target} running list`,
      summary: `${target} appeared in the controller running list.`,
      plainEnglish: `${target} appeared in the controller running list.`,
    };
  }

  if (event.actionCode === "TT") {
    const volts = getDetail(event.detailEntries, "VV");
    const amps = getDetail(event.detailEntries, "VA");
    return {
      title: `${subject} test`,
      summary: `${subject} recorded test results${volts ? ` with voltage ${volts}` : ""}.`,
      plainEnglish: `${subject} recorded test results.${
        volts ? ` Voltage values: ${volts}.` : ""
      }${amps ? ` Amp values: ${amps}.` : ""}`,
    };
  }

  if (event.actionCode === "LF" || event.actionCode === "LR") {
    const target = nameTarget(subject, { zone, program, poc, masterValve });
    return {
      title: `${target} learn flow`,
      summary: `${target} recorded learn-flow activity.`,
      plainEnglish: `${target} recorded learn-flow activity used by the controller to understand expected flow behavior.`,
    };
  }

  if (event.actionCode === "CC") {
    const target = nameTarget(subject, { zone, program, poc, masterValve, serial });
    return {
      title: `${target} configuration changed`,
      summary: `${target} configuration changed${detailText ? `: ${detailText}` : ""}.`,
      plainEnglish: `${target} configuration changed.${
        detailText ? ` Changed values: ${detailText}.` : ""
      }`,
    };
  }

  if (event.actionCode === "CN" || event.actionCode === "DC") {
    return {
      title: `${subject} ${event.actionCode === "CN" ? "connected" : "disconnected"}`,
      summary: `${subject} ${event.actionCode === "CN" ? "connected" : "disconnected"}.`,
      plainEnglish: `${subject} ${event.actionCode === "CN" ? "connected" : "disconnected"}.`,
    };
  }

  if (event.actionCode === "DS") {
    return {
      title: `${subject} disabled`,
      summary: `${subject} was disabled.`,
      plainEnglish: `${subject} was disabled. Review the details to see which controller setting or device was affected.`,
    };
  }

  const target = nameTarget(subject, { zone, program, poc, masterValve, serial });
  return {
    title: `${target} ${action}`,
    summary: `${target} recorded ${action}.`,
    plainEnglish: `${target} recorded ${action}. The trigger was ${trigger}.${
      detailText ? ` Details: ${detailText}.` : ""
    }`,
  };
}

function nameTarget(
  subject: string,
  fields: {
    zone?: string;
    program?: string;
    poc?: string;
    masterValve?: string;
    serial?: string;
  },
) {
  if (subject === "Zone" && fields.zone !== undefined) return `Zone ${fields.zone || "blank"}`;
  if (subject === "Program" && fields.program !== undefined)
    return `Program ${fields.program || "blank"}`;
  if (subject === "Point of Connection" && fields.poc !== undefined)
    return `Point of Connection ${fields.poc || "blank"}`;
  if (subject === "Master Valve" && fields.masterValve !== undefined)
    return `Master Valve ${fields.masterValve || "blank"}`;
  if (fields.serial) return `${subject} ${fields.serial}`;
  return subject;
}

export function summarizeUpload(uploadId: string, events: ParsedEvent[]): UploadSummary {
  const sorted = [...events].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  const categories = new Map<EventCategory, number>();
  const programs = new Set<string>();
  const zones = new Set<string>();
  const pocs = new Set<string>();
  const masterValves = new Set<string>();
  const moistureSensors = new Set<string>();

  sorted.forEach((event) => {
    categories.set(event.category, (categories.get(event.category) ?? 0) + 1);
    if (event.programNumber) programs.add(event.programNumber);
    if (event.zoneNumber) zones.add(event.zoneNumber);
    if (event.pointOfConnection) pocs.add(event.pointOfConnection);
    if (event.masterValve) masterValves.add(event.masterValve);
    if (event.moistureSensor || (event.subjectCode === "MS" && event.deviceSerialNumber)) {
      moistureSensors.add(event.moistureSensor ?? event.deviceSerialNumber!);
    }
  });

  return {
    uploadId,
    totalEvents: sorted.length,
    dateRangeStart: sorted[0]?.timestamp,
    dateRangeEnd: sorted[sorted.length - 1]?.timestamp,
    criticalIssues: sorted.filter((event) => event.severity === "CRITICAL").length,
    warnings: sorted.filter((event) => event.severity === "WARNING").length,
    mostActiveCategories: [...categories.entries()]
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count),
    programsSeen: sortNatural([...programs]),
    zonesSeen: sortNatural([...zones]),
    pointsOfConnectionSeen: sortNatural([...pocs]),
    masterValvesSeen: sortNatural([...masterValves]),
    moistureSensorsSeen: sortNatural([...moistureSensors]),
    firstEvent: sorted[0],
    lastEvent: sorted[sorted.length - 1],
  };
}

function sortNatural(values: string[]) {
  return values.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

function localIsoNow() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}T${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes(),
  ).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
}
